import React from "react"
import CheckinDisambiguationPanel, { DisambiguationView } from "./CheckinDisambiguationPanel"
import ProposalReviewPanel, { ProposedWriteView } from "./ProposalReviewPanel"
import { UserContext } from "./UserContext"
import { GenieMessage } from "../utils/genieMessages"

/**
 * A prepared bulk edit awaiting confirmation: compile and dry run are done, the
 * writes are stored as a draft, and the user is looking at what it would do.
 *
 * This is the panel behind every rule-shaped request — "delete my checkins from
 * Jan 5 through Jan 8", "add a dose to every day in March that's missing one" —
 * so it is where per-item approval has to work. Approving sends
 * `bulk_edit_confirmation` back over the chat socket, which the router turns
 * into `commit_bulk_edit_from_genie`.
 */

interface BulkEditPreparedPanelProps {
    message: GenieMessage
    socket: React.RefObject<WebSocket | null>
    /** Called when the user approves, so the page can show its typing state. */
    onApplying: () => void
    /**
     * True while this is the newest proposal panel in the chat — the one the
     * user is being asked about.
     *
     * Deliberately not "is the last message". Genie can speak after a proposal
     * without replacing it: when a correction turns out to be already
     * satisfied, the control loop replies in plain text and leaves the panel
     * standing. Gating approval on position meant that reply removed the
     * Approve button and left the user holding a proposal they could not
     * accept.
     */
    isLatestProposal: boolean
    formatDate: (dateString: string, includeYearIfDifferent?: boolean) => string
    /**
     * True once a sys:bulk_edit_committed narrative for this panel's
     * proposal_id has landed in the chat. Drops the transient "Applying…"
     * placeholder once the commit narrative takes over, and — since
     * `isLatestProposal` no longer goes false the moment anything follows the
     * panel — it is also what stops an already-applied proposal from offering
     * Approve a second time.
     */
    committed: boolean
    /**
     * Send a message as the user. Only used by the disambiguation choice, whose
     * answer is an ordinary turn rather than a confirmation on this proposal.
     */
    onSendMessage?: (message: string) => void
}

/**
 * Can this proposal be approved item by item?
 *
 * Two conditions, and both are about not committing something the reviewer
 * didn't see. The writes have to carry the draft's own `item_key`s — the commit
 * addresses writes by those and refuses a key it doesn't recognize, and a
 * proposal stored before the payload carried them has none to echo back. And
 * the panel has to hold the whole set: `summary_preview` is capped, so a subset
 * chosen out of a truncated preview would quietly drop every write past the cap.
 * Failing either, the panel stays approve-all, exactly as it was.
 */
export const isSelectable = (writes: ProposedWriteView[], totalWrites: number): boolean =>
    writes.length === totalWrites && writes.length > 0 && writes.every(w => !!w.item_key)

const BulkEditPreparedPanel: React.FC<BulkEditPreparedPanelProps> = ({
    message,
    socket,
    onApplying,
    isLatestProposal,
    formatDate,
    committed,
    onSendMessage,
}) => {
    const { user } = React.useContext(UserContext)
    const data = message.data || {}
    const disambiguation: DisambiguationView | null = data.disambiguation || null
    const proposalId: number | undefined = data.proposal_id
    const readback: string = data.readback || ""
    const warnings: string[] = data.warnings || []
    const notes: string[] = data.notes || []
    const candidateCount: number = data.candidate_count ?? 0
    const totalWrites: number = data.total_proposed_writes ?? (data.summary_preview?.length ?? 0)
    const truncated: boolean = !!data.truncated
    const previewWrites: ProposedWriteView[] = data.summary_preview || []
    // Which pass at this proposal the user is reading. Only worth saying once
    // there's been more than one — a first proposal being labelled "revision 1"
    // is noise, but after feedback the user wants to know they're looking at the
    // answer to it rather than at the panel they just commented on.
    const revision: number = data.revision ?? 1
    const [showDebug, setShowDebug] = React.useState(false)
    const [didConfirm, setDidConfirm] = React.useState(false)

    const selectable = isSelectable(previewWrites, totalWrites)

    // A draft that still has a question in it has no writes to review, and
    // approving it would apply nothing — so the panel is the question instead.
    if (disambiguation) {
        return (
            <CheckinDisambiguationPanel
                disambiguation={disambiguation}
                formatDate={formatDate}
                onPick={isLatestProposal ? onSendMessage : undefined}
            />
        )
    }

    const confirm = (selectedKeys?: string[]) => {
        if (!socket.current || socket.current.readyState !== WebSocket.OPEN || proposalId == null) return
        // Approving everything sends no keys at all, so the whole-proposal case
        // stays the request it has always been and never depends on the panel
        // and the stored draft agreeing on a key list.
        const subset = selectedKeys && selectedKeys.length < totalWrites ? selectedKeys : undefined
        setDidConfirm(true)
        onApplying()
        socket.current.send(JSON.stringify({
            type: "bulk_edit_confirmation",
            proposal_id: proposalId,
            ...(subset ? { item_keys: subset } : {}),
        }))
    }

    return (
        <ProposalReviewPanel
            writes={previewWrites}
            totalWrites={totalWrites}
            formatDate={formatDate}
            onApprove={confirm}
            showApprove={isLatestProposal && !didConfirm && !committed && proposalId != null}
            selectable={selectable}
            initialLimit={5}
            beforeList={
                <>
                    {revision > 1 && (
                        <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                            Revision {revision}
                        </div>
                    )}

                    {warnings.length > 0 && (
                        <div className="bg-yellow-50 border-l-2 border-yellow-500 p-2 mb-3 text-sm text-yellow-900">
                            <ul className="list-disc pl-5 space-y-1">
                                {warnings.map((w, i) => <li key={i}>{w}</li>)}
                            </ul>
                        </div>
                    )}

                    {totalWrites !== previewWrites.length && (
                        <div className="text-xs text-gray-500 mb-2">
                            Showing {previewWrites.length} of {totalWrites} proposed changes across {candidateCount} candidate{candidateCount === 1 ? '' : 's'}.
                        </div>
                    )}
                </>
            }
        >
            {didConfirm && !committed && (
                <div className="mb-4 text-gray-600 italic">Applying…</div>
            )}

            {/* Admin-only debug view: the raw readback, the dry run's notes about
                which candidates it skipped, and the per-write JSON. Kept so we can
                spot-check what the compiler/executor produced without leaving the
                chat. The notes name candidate keys and counts — that's a question
                about the rule, not something a reader of the proposal needs. */}
            {user?.isAdmin && (
                <div className="mt-3 border-t border-gray-200 pt-2">
                    <button
                        type="button"
                        onClick={() => setShowDebug(s => !s)}
                        className="text-xs text-gray-500 hover:text-gray-700 underline"
                    >
                        {showDebug ? "Hide debug details" : "Show debug details (admin)"}
                    </button>
                    {showDebug && (
                        <div className="sm:border-l-4 pl-3 sm:pl-4 py-3 mt-2 sm:border-dashed sm:border-gray-400 bg-gray-50">
                            {readback && (
                                <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono mb-3">{readback}</pre>
                            )}
                            <div className="text-xs text-gray-600 mb-2">
                                proposal_id={proposalId} • draft_id={data.draft_id ?? '—'} • revision={revision} • candidate_count={candidateCount} • total_writes={totalWrites}{truncated ? ` • truncated (showing ${previewWrites.length})` : ''}
                            </div>
                            {notes.length > 0 && (
                                <ul className="text-xs text-gray-600 list-disc pl-5 space-y-1 mb-2">
                                    {notes.map((n, i) => <li key={i}>{n}</li>)}
                                </ul>
                            )}
                            {previewWrites.length > 0 && (
                                <pre className="text-[11px] text-gray-700 whitespace-pre-wrap font-mono overflow-x-auto">
{JSON.stringify(previewWrites, null, 2)}
                                </pre>
                            )}
                        </div>
                    )}
                </div>
            )}
        </ProposalReviewPanel>
    )
}

export default BulkEditPreparedPanel
